import { useContext, useState } from 'react';
import api from './api/axiosInstance';
import { isProfileComplete } from './utils/userCompletion';
import { Container, Nav, Navbar, NavDropdown, Image, Offcanvas } from 'react-bootstrap';
import { UserContext } from './UserContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import defaultProfileImage from './assets/img/ic-img.svg';
import icChat from './assets/img/ic-chat.svg';
import icAngleLeft from './assets/img/ic-angle-left.svg';
import icBurger from './assets/img/ic-burger.svg';
import icCloseMobile from './assets/img/ic-close-mobile.svg';
import SidebarNavItems from './SidebarNavItems';
import { useHeaderAction } from './HeaderActionContext';
import { getDiscoveryParentPath } from './utils/discoveryParentPath';
import { DISCOVER_ROUTE } from './utils/navRoutes';
import { INCOMPLETE_PROFILE_REMINDER_SESSION_KEY } from './utils/onboarding';
import { resolveMediaUrl } from './utils/mediaUrl';

// Top-level pages reachable directly from the sidebar/offcanvas. Everything
// else is a sub-page, so the mobile header shows a back button instead of
// the support shortcut.
const MOBILE_PARENT_PATHS = new Set([
    '/discovery/countries',
    '/discovery/cities',
    '/discovery/companies',
    '/discovery/companies/jobs',
    '/discovery/jobs',
    '/support',
    '/communities',
    '/profile',
    '/ticket-list',
    '/onboarding-company-list',
    '/onboarding-user-list',
    '/onboarding-city-list',
    '/onboarding-country-list',
    '/donations-my',
    '/donations-list',
    '/reporting/jobs',
    '/reporting/general',
    '/reporting/users',
    '/reporting/revenue',
]);

// Profile and all its sub-routes (/profile, /profile/academics, ...).
const isProfilePath = (pathname) => /^\/profile(\/|$)/.test(String(pathname || ''));

const isMobileParentPage = (pathname) => {
    const path = String(pathname || '').replace(/\/+$/, '');
    return MOBILE_PARENT_PATHS.has(path || '/');
};

// Mobile-only header title, grouped by top-level section (mirrors the route
// groupings in Breadcrumb.js). Detail sub-pages fall back to their section's name.
const getMobileHeaderTitle = (pathname) => {
    const path = String(pathname || '');
    if (path.startsWith('/discovery')) return 'Discovery';
    if (path.startsWith('/user') || path === '/onboarding-user-list') return 'Users';
    if (path.startsWith('/city') || path === '/onboarding-city-list') return 'Cities';
    if (path.startsWith('/company') || path === '/onboarding-company-list') return 'Companies';
    if (path.startsWith('/country') || path === '/onboarding-country-list') return 'Countries';
    if (path.startsWith('/ticket')) return 'Tickets';
    if (path.startsWith('/donations')) return 'Donations';
    if (path.startsWith('/support')) return 'Support';
    if (path.startsWith('/communities')) return 'Communities';
    if (path.startsWith('/reporting')) return 'Reporting';
    if (path.startsWith('/profile')) return 'Profile';
    if (path.startsWith('/mapping')) return 'Mapping';
    if (path.startsWith('/register')) return 'Register';
    if (path === '/onboarding' || path.startsWith('/onboarding/')) return 'Complete profile';
    return '';
};

const Header = ({ ticketCount = 0, headerClassName = '' }) => {
    const { user, setUser } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const { mobileLeftAction } = useHeaderAction();

    const mobileHeaderTitle = getMobileHeaderTitle(location.pathname);

    const handleMobileBack = () => {
        const discoveryParentPath = getDiscoveryParentPath(location.pathname);
        navigate(discoveryParentPath || -1);
    };

    const handleProfileBack = () => navigate(DISCOVER_ROUTE);

    const handleLogout = async () => {
        try {
            await api.post('/api/auth/logout');
        } catch (err) {
            console.warn('Logout request failed (continuing):', err?.message || err);
        }

        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem(INCOMPLETE_PROFILE_REMINDER_SESSION_KEY);
        setUser(null);
        navigate('/', { replace: true });
    };

const shouldShowCompleteProfile =
    String(user?.role || '').trim().toLowerCase() === 'student' &&
    !isProfileComplete(user);

    return (
        <header className={`header flex-grow-1 ${headerClassName}`}>
            <Container>
                <Navbar expand="lg" className="align-items-center">

                    {/* ✅ Mobile: support icon, page title, burger opens the offcanvas sidebar */}
                    <div className="mobile-header-row d-lg-none w-100">
                        <div className="mobile-header-actions">
                            {isProfilePath(location.pathname) ? (
                                <button
                                    type="button"
                                    className="mobile-icon-btn"
                                    aria-label="Back to Discovery"
                                    onClick={handleProfileBack}
                                >
                                    <img src={icAngleLeft} alt="" />
                                </button>
                            ) : mobileLeftAction?.type === 'back' ? (
                                <button
                                    type="button"
                                    className="mobile-icon-btn"
                                    aria-label={mobileLeftAction.ariaLabel || 'Back'}
                                    onClick={mobileLeftAction.onClick}
                                >
                                    <img src={icAngleLeft} alt="" />
                                </button>
                            ) : mobileLeftAction?.mapFirst ? (
                                <>
                                    {mobileLeftAction.showBack && (
                                        <button
                                            type="button"
                                            className="mobile-icon-btn"
                                            aria-label="Back"
                                            onClick={handleMobileBack}
                                        >
                                            <img src={icAngleLeft} alt="" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="mobile-icon-btn"
                                        aria-label={mobileLeftAction.ariaLabel || 'Open action'}
                                        onClick={mobileLeftAction.onClick}
                                    >
                                        <img src={icChat} alt="" />
                                    </button>
                                </>
                            ) : !isMobileParentPage(location.pathname) ? (
                                <button
                                    type="button"
                                    className="mobile-icon-btn"
                                    aria-label="Back"
                                    onClick={handleMobileBack}
                                >
                                    <img src={icAngleLeft} alt="" />
                                </button>
                            ) : mobileLeftAction ? (
                                <button
                                    type="button"
                                    className="mobile-icon-btn"
                                    aria-label={mobileLeftAction.ariaLabel || 'Open action'}
                                    onClick={mobileLeftAction.onClick}
                                >
                                    <img src={icChat} alt="" />
                                </button>
                            ) : (
                                <Link
                                    to="/support"
                                    className="mobile-icon-btn"
                                    aria-label="Support"
                                    onClick={() => setShowMenu(false)}
                                >
                                    <img src={icChat} alt="" />
                                </Link>
                            )}
                        </div>

                        <h1 className="mobile-page-title mb-0">{mobileHeaderTitle}</h1>

                        <button
                            type="button"
                            className="mobile-icon-btn mobile-header-menu"
                            aria-label="Open navigation"
                            onClick={() => setShowMenu(true)}
                        >
                            <img src={icBurger} alt="" />
                        </button>
                    </div>

                    {/* ✅ Desktop only: dropdown on RHS */}
                    <Navbar.Collapse id="basic-navbar-nav">
                        <Nav className="ms-auto align-items-center">
                            <div className="d-none d-lg-block">
                                <NavDropdown
                                    show={dropdownOpen}
                                    onToggle={() => setDropdownOpen(!dropdownOpen)}
                                    title={
                                        <>
                                            <Image
                                                src={
                                                    typeof user?.profileImage === 'string' && user.profileImage.length > 0
                                                        ? resolveMediaUrl(user.profileImage)
                                                        : defaultProfileImage
                                                }
                                                alt={user?.firstName || 'User'}
                                                className="round"
                                            />

                                            <span className="d-inline-flex align-items-center">
                                                {user?.familyName && user?.firstName
                                                    ? `${user.familyName} ${user.firstName}`
                                                    : user?.role
                                                }
                                                {dropdownOpen ? (
                                                    <FaChevronUp className="ms-2" />
                                                ) : (
                                                    <FaChevronDown className="ms-2" />
                                                )}
                                            </span>
                                        </>
                                    }
                                    id="basic-nav-dropdown"
                                    align="end"
                                >
                                    <>
                                        {shouldShowCompleteProfile && (
                                            <NavDropdown.Item
                                                as="button"
                                                onClick={() => navigate('/onboarding')}
                                                className="bg-transparent border-0 text-start w-100"
                                            >
                                                Complete Profile
                                            </NavDropdown.Item>
                                        )}

                                        <NavDropdown.Item
                                            as="button"
                                            onClick={handleLogout}
                                            className="bg-transparent border-0 text-start w-100"
                                        >
                                            Logout
                                        </NavDropdown.Item>
                                    </>
                                </NavDropdown>
                            </div>
                        </Nav>
                    </Navbar.Collapse>

                    {/* ✅ Mobile offcanvas */}
                    <Offcanvas
                        show={showMenu}
                        onHide={() => setShowMenu(false)}
                        placement="start"
                        className="d-lg-none mobile-nav"
                    >
                        <Offcanvas.Header className="mobile-nav__header">
                            <Offcanvas.Title className="mobile-nav__title mobile-page-title">
                                {mobileHeaderTitle}
                            </Offcanvas.Title>
                            <button
                                type="button"
                                className="mobile-nav__close"
                                aria-label="Close navigation"
                                onClick={() => setShowMenu(false)}
                            >
                                <img src={icCloseMobile} alt="" />
                            </button>
                        </Offcanvas.Header>

                        {/* ✅ make it flex column so bottom section can stick */}
                        <Offcanvas.Body className="sidebar d-flex flex-column">
                            <SidebarNavItems
                                ticketCount={ticketCount}
                                onNavigate={() => setShowMenu(false)}
                                variant="offcanvas"
                            />

                            {/* ✅ Mobile user/logout at bottom */}
                            <div className="sidebar-user mt-auto">

                                {/* ✅ User row = foldout trigger (same structure as sidebar items) */}
                                <button
                                    role="button"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault(); // ✅
                                        setUserMenuOpen(!userMenuOpen); // ✅
                                    }}
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ width: '100%' }}
                                >
                                    <div className="d-flex align-items-center">
                                        <Image
                                            src={
                                                typeof user?.profileImage === 'string' && user.profileImage.length > 0
                                                    ? resolveMediaUrl(user.profileImage)
                                                    : defaultProfileImage
                                            }
                                            alt={user?.firstName || 'User'}
                                            className="round"
                                        />
                                        {user?.familyName && user?.firstName
                                            ? `${user.familyName} ${user.firstName}`
                                            : (user?.role || 'User')
                                        }
                                    </div>

                                    {userMenuOpen ? <BsChevronUp /> : <BsChevronDown />}
                                </button>

                                {/* ✅ Submenu */}
                                {userMenuOpen && (
                                    <div className="nav flex-column submenu">
                                        {shouldShowCompleteProfile && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigate('/onboarding');
                                                    setShowMenu(false);
                                                }}
                                                className="nav-link bg-transparent border-0 text-start w-100"
                                            >
                                                Complete Profile
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleLogout();
                                                setShowMenu(false);
                                            }}
                                            className="nav-link bg-transparent border-0 text-start w-100"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>

                        </Offcanvas.Body>
                    </Offcanvas>

                </Navbar>
            </Container>
        </header>
    );
};

export default Header;
